import { motion, AnimatePresence } from "framer-motion";
import { Activity, ShieldAlert, AlertTriangle, CheckCircle2, Cpu, HardDrive, Wifi, Radio, Cloud, ArrowRight, Brain, Send, ChevronDown, Users, Shield } from "lucide-react";
import { useEffect, useState } from "react";

/* ============================================================
   ArchitectureFlow — Sensors → Nodes → Gateway → Cloud → User
   The cleanest one-shot explainer of how Nexus is wired.
============================================================ */
export function NexusArchitectureFlow() {
  return (
    <div data-testid="nexus-architecture" className="bg-geminii-card border border-nexus-blue/20 rounded-2xl p-6 md:p-10 overflow-hidden">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">System architecture</span>
      </div>
      <h3 className="font-display font-bold text-xl md:text-2xl text-white mb-1">From the asset to the operator — in milliseconds.</h3>
      <p className="text-sm text-geminii-muted mb-8 max-w-2xl">Every layer is engineered so the operation keeps running when one of them blinks.</p>

      <div className="grid grid-cols-5 gap-2 md:gap-4 items-stretch">
        {[
          { label: "Sensors", sub: "Pressure · Flow · Energy", color: "from-slate-500/20 to-slate-500/5" },
          { label: "Nexus Node", sub: "I/O + Scripting + Radio", color: "from-nexus-blue/30 to-nexus-blue/10", emphasis: true, Icon: Cpu },
          { label: "Nexus Gateway", sub: "Aggregation + Edge logic", color: "from-nexus-blue/30 to-nexus-blue/10", emphasis: true, Icon: HardDrive },
          { label: "Geminii Cloud", sub: "Tag DB + Rules + AI", color: "from-cerebrum-violet/30 to-cerebrum-violet/10", Icon: Cloud },
          { label: "Operator", sub: "Browser · Mobile · API", color: "from-rogue-cyan/30 to-rogue-cyan/10", Icon: Activity },
        ].map((node, i) => (
          <div key={i} className="relative">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`bg-gradient-to-br ${node.color} border ${node.emphasis ? "border-nexus-blue/40" : "border-white/[0.06]"} rounded-xl p-3 md:p-4 h-full flex flex-col items-center text-center`}
            >
              {node.Icon ? (
                <node.Icon size={18} className="text-white/80 mb-2" />
              ) : (
                <div className="w-3 h-3 rounded-full bg-white/40 mb-2" />
              )}
              <p className="font-display font-bold text-[11px] md:text-sm text-white leading-tight">{node.label}</p>
              <p className="text-[9px] md:text-[10px] text-geminii-dim mt-1 leading-tight">{node.sub}</p>
            </motion.div>
            {i < 4 && (
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.4, delay: 0.5 + i * 0.1 }}
                className="absolute top-1/2 -right-2 w-4 h-px bg-nexus-blue/40 origin-left hidden md:block"
              />
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[11px] text-geminii-dim">
        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-nexus-blue" /> Sub-second telemetry</span>
        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-cerebrum-violet" /> Edge buffering on uplink loss</span>
        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-rogue-emerald" /> Local control survives the cloud</span>
      </div>
    </div>
  );
}

/* ============================================================
   LiveTelemetryVisual — animated gauges + a sparkline
============================================================ */
export function NexusTelemetryVisual() {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((x) => x + 1), 800);
    return () => clearInterval(id);
  }, []);

  const tanks = [
    { label: "Reservoir 03", base: 68, amp: 4, unit: "%", critical: 20 },
    { label: "Bore pump-A", base: 6.2, amp: 0.3, unit: "bar" },
    { label: "Line flow", base: 184, amp: 22, unit: "L/min" },
    { label: "Motor draw", base: 22.4, amp: 1.6, unit: "A", warn: 24 },
  ];

  return (
    <div data-testid="nexus-telemetry" className="bg-geminii-card border border-white/[0.06] rounded-2xl p-6 md:p-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Live dashboard</span>
          <h3 className="font-display font-bold text-lg text-white mt-1">Site · Springbok pump house</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rogue-emerald opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-rogue-emerald" />
          </span>
          <span className="text-[10px] font-mono text-rogue-emerald uppercase tracking-wider">Streaming</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tanks.map((tank, i) => {
          const v = (tank.base + Math.sin((t + i * 2) / 2) * tank.amp).toFixed(tank.amp < 1 ? 2 : 1);
          const isWarn = tank.warn && parseFloat(v) >= tank.warn;
          return (
            <div key={tank.label} className={`rounded-lg border p-3 ${isWarn ? "bg-amber-500/5 border-amber-500/30" : "bg-white/[0.02] border-white/[0.06]"}`}>
              <p className="text-[10px] text-geminii-dim font-mono uppercase tracking-wider">{tank.label}</p>
              <p className="font-display font-bold text-2xl text-white mt-1 tabular-nums">{v}<span className="text-xs text-geminii-muted ml-1 font-mono">{tank.unit}</span></p>
              <div className="mt-2 h-1 bg-white/[0.04] rounded-full overflow-hidden">
                <motion.div
                  animate={{ width: `${30 + Math.abs(Math.sin((t + i) / 2)) * 65}%` }}
                  transition={{ duration: 0.6 }}
                  className={`h-full rounded-full ${isWarn ? "bg-amber-400" : "bg-nexus-blue"}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider">Reservoir 03 · 24 h trend</span>
          <span className="text-[10px] font-mono text-geminii-dim">128 samples</span>
        </div>
        <svg viewBox="0 0 320 60" className="w-full h-12">
          <defs>
            <linearGradient id="trendFill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#1FB6C1" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#1FB6C1" stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.path
            d="M0,40 L20,38 L40,42 L60,28 L80,32 L100,22 L120,28 L140,18 L160,24 L180,14 L200,20 L220,26 L240,18 L260,28 L280,22 L300,18 L320,24"
            stroke="#1FB6C1"
            strokeWidth="1.5"
            fill="none"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.5 }}
          />
          <path d="M0,40 L20,38 L40,42 L60,28 L80,32 L100,22 L120,28 L140,18 L160,24 L180,14 L200,20 L220,26 L240,18 L260,28 L280,22 L300,18 L320,24 L320,60 L0,60 Z" fill="url(#trendFill)" />
        </svg>
      </div>
    </div>
  );
}

/* ============================================================
   AlarmConsole — prioritised live alarm feed
============================================================ */
export function NexusAlarmConsole() {
  const alarms = [
    { sev: "high", asset: "Pump-A · pressure", msg: "Discharge pressure above 7.8 bar setpoint", site: "Springbok", t: "00:12s", ack: false },
    { sev: "medium", asset: "Tank-03 · level", msg: "Level falling at 2.4 %/min — projected empty in 18 min", site: "Springbok", t: "01:38s", ack: false },
    { sev: "info", asset: "Gateway · uplink", msg: "Cellular RSSI degraded to -97 dBm; failover armed", site: "Aggeneys", t: "04:11s", ack: false },
    { sev: "low", asset: "Solar · array-2", msg: "Charge current below daily baseline by 12%", site: "Aggeneys", t: "12:46s", ack: true },
  ];
  const sevColor = (s) => ({
    high: "border-red-500/40 bg-red-500/[0.05] text-red-300",
    medium: "border-amber-500/40 bg-amber-500/[0.05] text-amber-300",
    low: "border-rogue-cyan/30 bg-rogue-cyan/[0.04] text-rogue-cyan",
    info: "border-white/[0.1] bg-white/[0.02] text-geminii-muted",
  }[s]);

  return (
    <div data-testid="nexus-alarms" className="bg-geminii-card border border-white/[0.06] rounded-2xl p-6 md:p-8">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-400" />
          <h3 className="font-display font-bold text-lg text-white">Active alarms</h3>
          <span className="text-[10px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded">3 unacknowledged</span>
        </div>
        <span className="text-[10px] font-mono text-geminii-dim">Sorted by severity</span>
      </div>
      <div className="space-y-2">
        {alarms.map((a, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: a.ack ? 0.5 : 1, x: 0 }}
            transition={{ delay: i * 0.08 }}
            className={`border rounded-lg p-3 flex items-center justify-between ${sevColor(a.sev)}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono uppercase tracking-wider opacity-80">{a.sev}</span>
                <span className="font-display font-semibold text-sm text-white">{a.asset}</span>
                <span className="text-[10px] text-geminii-dim">· {a.site}</span>
              </div>
              <p className="text-xs text-geminii-muted truncate">{a.msg}</p>
            </div>
            <div className="flex items-center gap-3 ml-3 flex-shrink-0">
              <span className="text-[10px] font-mono text-geminii-dim">{a.t}</span>
              {a.ack ? (
                <CheckCircle2 size={14} className="text-rogue-emerald" />
              ) : (
                <button className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded border border-white/[0.1] hover:bg-white/[0.04] transition-colors">Ack</button>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   RuleBuilderVisual — conversational rule authoring mockup
============================================================ */
export function NexusRuleBuilder() {
  return (
    <div data-testid="nexus-rule-builder" className="bg-geminii-card border border-cerebrum-violet/20 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert size={16} className="text-cerebrum-violet" />
        <span className="text-[10px] font-mono text-cerebrum-violet uppercase tracking-wider">Conversational rule authoring</span>
      </div>
      <h3 className="font-display font-bold text-lg text-white mb-4">Describe the intent. The platform writes the rule.</h3>

      <div className="bg-black/30 rounded-lg border border-white/[0.06] p-4 mb-3">
        <p className="text-[10px] font-mono text-geminii-dim mb-2 uppercase tracking-wider">Operator intent</p>
        <p className="text-sm text-white leading-relaxed">
          <span className="text-cerebrum-violet">"</span>If reservoir-03 falls below 20% and pump-A has been idle for 90 seconds, open bypass valve-B, alert the on-call engineer, and log to compliance.<span className="text-cerebrum-violet">"</span>
        </p>
      </div>

      <div className="bg-black/30 rounded-lg border border-cerebrum-violet/20 p-4 mb-3">
        <p className="text-[10px] font-mono text-cerebrum-violet mb-2 uppercase tracking-wider">Compiled logic preview</p>
        <pre className="text-[11px] font-mono text-white/80 leading-relaxed overflow-x-auto">
{`WHEN
  reservoir_03.level < 20%
  AND pump_a.state == "off" FOR 90s
THEN
  valve_b.open()
  notify(role: "on-call", channel: ["sms","whatsapp"])
  audit.compliance("low-level-bypass")`}
        </pre>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="bg-rogue-emerald/5 border border-rogue-emerald/20 rounded-lg p-2.5">
          <p className="text-[9px] font-mono text-rogue-emerald uppercase tracking-wider">Back-test · 30 d</p>
          <p className="font-display font-bold text-base text-white mt-0.5">3 fires</p>
          <p className="text-[10px] text-geminii-dim">All correct</p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-2.5">
          <p className="text-[9px] font-mono text-geminii-dim uppercase tracking-wider">False positives</p>
          <p className="font-display font-bold text-base text-white mt-0.5">0</p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-2.5">
          <p className="text-[9px] font-mono text-geminii-dim uppercase tracking-wider">Status</p>
          <p className="font-display font-bold text-base text-rogue-emerald mt-0.5">Ready</p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   NexiChatVisual — natural-language operator query
============================================================ */
export function NexusNexiChat() {
  return (
    <div data-testid="nexus-nexi" className="bg-geminii-card border border-cerebrum-violet/20 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-2 mb-5">
        <Brain size={16} className="text-cerebrum-violet" />
        <span className="text-[10px] font-mono text-cerebrum-violet uppercase tracking-wider">Nexi · plain-English operator co-pilot</span>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex justify-end">
          <div className="bg-rogue-cyan/10 border border-rogue-cyan/20 rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[80%]">
            <p className="text-sm text-white">What is the biggest risk on the Northern Cape sites right now?</p>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="flex justify-start">
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl rounded-bl-sm px-4 py-3 max-w-[88%]">
            <p className="text-sm text-white leading-relaxed mb-3">
              Pump-A at Aggeneys is the highest-priority risk. Discharge pressure has trended <span className="text-amber-300">up 14%</span> over 48 hours while motor draw is steady — pattern matches the partial-blockage signature seen in 2024-Q4. Recommended action: schedule strainer inspection within 12 hours.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-amber-500/5 border border-amber-500/20 rounded p-2">
                <p className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">Confidence</p>
                <p className="font-display font-bold text-sm text-white">High</p>
              </div>
              <div className="bg-white/[0.02] border border-white/[0.06] rounded p-2">
                <p className="text-[9px] font-mono text-geminii-dim uppercase tracking-wider">Asset</p>
                <p className="font-display font-bold text-sm text-white">Pump-A</p>
              </div>
              <div className="bg-white/[0.02] border border-white/[0.06] rounded p-2">
                <p className="text-[9px] font-mono text-geminii-dim uppercase tracking-wider">Window</p>
                <p className="font-display font-bold text-sm text-white">12 h</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="flex items-center gap-2 bg-white/[0.03] border border-white/[0.06] rounded-full pl-4 pr-2 py-2">
        <input
          readOnly
          value="Ask anything across every site, gateway, and tag…"
          className="flex-1 bg-transparent text-sm text-geminii-dim outline-none"
        />
        <button className="w-7 h-7 rounded-full bg-cerebrum-violet/20 border border-cerebrum-violet/40 flex items-center justify-center">
          <Send size={12} className="text-cerebrum-violet" />
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   CommsRangeVisual — visualises radio range options
============================================================ */
export function NexusCommsRange() {
  const items = [
    { name: "Wi-Fi Direct", w: 12, color: "bg-rogue-cyan/70", label: "≤ 200 m" },
    { name: "LoRa (line of sight)", w: 65, color: "bg-nexus-blue/70", label: "≤ 15 km" },
    { name: "LoRa + repeater chain", w: 92, color: "bg-nexus-blue", label: "≤ 40 km" },
    { name: "Cellular", w: 100, color: "bg-cerebrum-violet/70", label: "Anywhere with coverage" },
  ];
  return (
    <div data-testid="nexus-comms" className="bg-geminii-card border border-white/[0.06] rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-2 mb-1">
        <Radio size={16} className="text-nexus-blue" />
        <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Connectivity</span>
      </div>
      <h3 className="font-display font-bold text-lg text-white mb-1">Pick the right radio per asset.</h3>
      <p className="text-sm text-geminii-muted mb-6 max-w-lg">A single gateway can run multiple radios. Mix protocols on the same site — short-range mesh indoors, long-range LoRa outdoors, cellular failover everywhere.</p>

      <div className="space-y-4">
        {items.map((it, i) => (
          <div key={it.name}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-white">{it.name}</span>
              <span className="text-[10px] font-mono text-geminii-dim">{it.label}</span>
            </div>
            <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${it.w}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                className={`h-full ${it.color} rounded-full`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   DashboardCanvasVisual — drag-drop dashboard builder mockup
============================================================ */
export function NexusDashboardCanvas() {
  return (
    <div data-testid="nexus-dashboard-canvas" className="bg-geminii-card border border-white/[0.06] rounded-2xl p-6 md:p-8 overflow-hidden">
      <div className="flex items-center justify-between mb-5">
        <div>
          <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Operator-built dashboards</span>
          <h3 className="font-display font-bold text-lg text-white mt-1">A canvas. A widget library. Done.</h3>
        </div>
        <span className="text-[10px] font-mono text-geminii-dim hidden sm:inline">No consultants. No reports.</span>
      </div>

      <div className="grid grid-cols-6 gap-2">
        {/* gauge */}
        <div className="col-span-2 row-span-2 bg-gradient-to-br from-nexus-blue/15 to-nexus-blue/5 border border-nexus-blue/20 rounded-lg p-3 flex flex-col">
          <p className="text-[9px] font-mono text-geminii-dim uppercase tracking-wider">Gauge</p>
          <div className="flex-1 flex items-center justify-center">
            <div className="relative w-20 h-20">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
                <motion.circle
                  cx="18" cy="18" r="14" fill="none" stroke="#1FB6C1" strokeWidth="3"
                  strokeLinecap="round" strokeDasharray="88"
                  initial={{ strokeDashoffset: 88 }}
                  whileInView={{ strokeDashoffset: 24 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2 }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="font-display font-bold text-base text-white">72%</p>
              </div>
            </div>
          </div>
        </div>

        {/* sparkline x2 */}
        {[1,2].map((i) => (
          <div key={i} className="col-span-2 bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
            <p className="text-[9px] font-mono text-geminii-dim uppercase tracking-wider">Trend</p>
            <svg viewBox="0 0 100 20" className="w-full h-8 mt-1">
              <motion.path
                d={i === 1 ? "M0,12 L20,10 L40,14 L60,6 L80,10 L100,4" : "M0,8 L20,12 L40,6 L60,14 L80,8 L100,10"}
                stroke="#1FB6C1" strokeWidth="1.2" fill="none"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.1 * i }}
              />
            </svg>
          </div>
        ))}

        {/* alarm card */}
        <div className="col-span-2 bg-amber-500/5 border border-amber-500/30 rounded-lg p-3">
          <p className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">Alarms</p>
          <p className="font-display font-bold text-2xl text-white mt-1 tabular-nums">3</p>
          <p className="text-[10px] text-geminii-dim">2 high · 1 medium</p>
        </div>

        {/* kpi */}
        <div className="col-span-2 bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
          <p className="text-[9px] font-mono text-geminii-dim uppercase tracking-wider">Uptime · 7 d</p>
          <p className="font-display font-bold text-2xl text-rogue-emerald mt-1 tabular-nums">99.6%</p>
        </div>

        {/* mini bar chart */}
        <div className="col-span-4 bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
          <p className="text-[9px] font-mono text-geminii-dim uppercase tracking-wider mb-2">Energy by site · today</p>
          <div className="flex items-end gap-1.5 h-12">
            {[40, 75, 60, 90, 55, 70, 85, 50].map((h, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                whileInView={{ height: `${h}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.04 }}
                className="flex-1 bg-nexus-blue/70 rounded-sm"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   HardwareCardVisual — depicts a Nexus Node / Gateway with status
============================================================ */
export function NexusHardwareShowcase() {
  return (
    <div data-testid="nexus-hardware-card" className="bg-geminii-card border border-nexus-blue/20 rounded-2xl p-6 md:p-10 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div>
          <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Geminii Nexus Node · Mk II</span>
          <h3 className="font-display font-bold text-2xl text-white mt-2 mb-3">Sense anything. Survive anywhere.</h3>
          <p className="text-sm text-geminii-muted leading-relaxed mb-5">
            IP65 industrial enclosure with eight universal I/O channels, on-board scripting, and a multi-radio stack. Designed in-house, manufactured to outlive the project that ordered it.
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[
              { k: "Channels", v: "8 universal I/O" },
              { k: "Radios", v: "LoRa + Wi-Fi + optional cellular" },
              { k: "Power", v: "Battery · Solar · DC" },
              { k: "Rating", v: "IP65 · -20 to +70 °C" },
            ].map((s) => (
              <div key={s.k} className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-2.5">
                <p className="text-[9px] font-mono text-geminii-dim uppercase tracking-wider">{s.k}</p>
                <p className="text-xs text-white mt-0.5">{s.v}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Visual mock of the device */}
        <div className="relative aspect-square max-w-sm mx-auto w-full">
          <div className="absolute inset-0 bg-gradient-to-br from-nexus-blue/10 to-transparent rounded-2xl" />
          <div className="absolute inset-6 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl border border-white/10 shadow-2xl flex flex-col">
            <div className="p-4 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-rogue-emerald shadow-[0_0_8px_rgba(16,185,129,0.6)] animate-pulse" />
                <span className="text-[9px] font-mono text-white/60 uppercase tracking-wider">NEXUS · NODE</span>
              </div>
              <Cpu size={12} className="text-white/40" />
            </div>
            <div className="flex-1 p-4 grid grid-cols-4 gap-1.5 content-center">
              {Array.from({ length: 8 }).map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0.3 }}
                  animate={{ opacity: [0.3, 1, 0.5] }}
                  transition={{ duration: 2, delay: i * 0.15, repeat: Infinity }}
                  className="aspect-square rounded bg-nexus-blue/30 border border-nexus-blue/40"
                />
              ))}
            </div>
            <div className="px-4 pb-3 flex items-center justify-between">
              <div className="flex gap-1">
                <Wifi size={9} className="text-white/40" />
                <Radio size={9} className="text-nexus-blue" />
              </div>
              <span className="text-[8px] font-mono text-white/40">CH-08 · RSSI -72</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   IndustryCloud — animated industry breadth tag rail
============================================================ */
export function NexusIndustryRail({ industries }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {industries.map((ind, i) => (
        <motion.div
          key={ind.name}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: i * 0.06 }}
          className="bg-geminii-card border border-white/[0.06] hover:border-nexus-blue/30 rounded-xl p-5 transition-colors"
        >
          <ind.icon size={20} className="text-nexus-blue mb-3" />
          <h4 className="font-display font-bold text-sm text-white mb-1.5">{ind.name}</h4>
          <p className="text-xs text-geminii-muted leading-relaxed">{ind.copy}</p>
        </motion.div>
      ))}
    </div>
  );
}

/* ============================================================
   IndustrySelector — expandable per-sector deep dive
============================================================ */
export function NexusIndustrySelector({ industries }) {
  const [open, setOpen] = useState(industries[0]?.slug);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="nexus-industry-selector">
      {/* Industry rail */}
      <div className="lg:col-span-4">
        <div className="space-y-2">
          {industries.map((ind) => {
            const active = open === ind.slug;
            return (
              <button
                key={ind.slug}
                onClick={() => setOpen(active ? null : ind.slug)}
                data-testid={`industry-pill-${ind.slug}`}
                className={`w-full text-left rounded-xl border p-4 transition-all flex items-start gap-3 ${
                  active
                    ? "bg-nexus-blue/10 border-nexus-blue/40"
                    : "bg-geminii-card border-white/[0.06] hover:border-nexus-blue/25"
                }`}
              >
                <ind.icon size={18} className={active ? "text-nexus-blue mt-0.5" : "text-geminii-muted mt-0.5"} />
                <div className="flex-1 min-w-0">
                  <h4 className={`font-display font-bold text-sm ${active ? "text-white" : "text-white/90"}`}>
                    {ind.name}
                  </h4>
                  <p className="text-[11px] text-geminii-dim mt-1 leading-snug">{ind.tagline}</p>
                </div>
                <ChevronDown
                  size={14}
                  className={`text-geminii-dim flex-shrink-0 transition-transform mt-1 ${active ? "rotate-180 text-nexus-blue" : ""}`}
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail panel */}
      <div className="lg:col-span-8">
        <AnimatePresence mode="wait">
          {industries.map((ind) =>
            open === ind.slug ? (
              <motion.div
                key={ind.slug}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="bg-geminii-card border border-nexus-blue/15 rounded-2xl p-6 md:p-8"
              >
                <div className="flex items-center gap-3 mb-3">
                  <ind.icon size={20} className="text-nexus-blue" />
                  <h3 className="font-display font-bold text-xl text-white">{ind.name}</h3>
                </div>
                <p className="text-sm text-geminii-muted leading-relaxed mb-6">{ind.intro}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <DetailBlock title="Typical assets" items={ind.typicalAssets} accent="text-white/90" />
                  <DetailBlock title="Sensor stack" items={ind.sensors} accent="text-white/90" />
                  <DetailBlock title="Example rules" items={ind.rules} accent="text-nexus-blue" />
                  <DetailBlock title="Analytics outputs" items={ind.analytics} accent="text-cerebrum-violet" />
                </div>
              </motion.div>
            ) : null
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function DetailBlock({ title, items, accent = "text-white/90" }) {
  return (
    <div>
      <p className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider mb-2">{title}</p>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-xs leading-relaxed">
            <span className={`mt-1.5 w-1 h-1 rounded-full flex-shrink-0 ${accent.replace("text-", "bg-")}`} />
            <span className={accent}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ============================================================
   RolesGrid — multi-user role visualisation
============================================================ */
export function NexusRolesGrid({ roles }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="nexus-roles-grid">
      {roles.map((r, i) => (
        <motion.div
          key={r.name}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.35, delay: i * 0.05 }}
          className="bg-geminii-card border border-white/[0.06] hover:border-nexus-blue/25 rounded-xl p-5 transition-colors"
        >
          <div className="flex items-center gap-2 mb-3">
            <Shield size={14} className="text-nexus-blue" />
            <h4 className="font-display font-bold text-sm text-white uppercase tracking-wider">{r.name}</h4>
          </div>
          <p className="text-xs text-geminii-muted leading-relaxed mb-4">{r.desc}</p>
          <ul className="space-y-1.5">
            {r.rights.map((right) => (
              <li key={right} className="flex items-start gap-2 text-[11px] text-white/85">
                <CheckCircle2 size={11} className="text-nexus-blue mt-0.5 flex-shrink-0" />
                <span>{right}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      ))}
    </div>
  );
}

/* ============================================================
   UserOrgVisual — depicts an org with multiple staff in roles
============================================================ */
export function NexusUserOrgVisual() {
  const members = [
    { initials: "JM", name: "J. Marais", role: "Owner", color: "bg-nexus-blue/30 border-nexus-blue/50" },
    { initials: "AT", name: "A. Theron", role: "Administrator", color: "bg-cerebrum-violet/30 border-cerebrum-violet/50" },
    { initials: "SP", name: "S. Pillay", role: "Operator", color: "bg-rogue-cyan/30 border-rogue-cyan/50" },
    { initials: "RD", name: "R. Dlamini", role: "Technician", color: "bg-amber-500/25 border-amber-500/45" },
    { initials: "KV", name: "K. Vermeulen", role: "Analyst", color: "bg-rogue-emerald/25 border-rogue-emerald/45" },
    { initials: "EL", name: "E. Louw", role: "Client", color: "bg-white/[0.08] border-white/15" },
  ];
  return (
    <div className="bg-geminii-card border border-white/[0.06] rounded-2xl p-6 md:p-8" data-testid="nexus-org-visual">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Geminii Demo · Owner</p>
          <h4 className="font-display font-bold text-base text-white mt-1">6 active members · 4 active sites</h4>
        </div>
        <button className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 rounded-full bg-nexus-blue/15 border border-nexus-blue/35 text-nexus-blue flex items-center gap-1.5">
          <Users size={11} /> Invite member
        </button>
      </div>

      <div className="space-y-2">
        {members.map((m) => (
          <div key={m.name} className="flex items-center gap-3 bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
            <div className={`w-9 h-9 rounded-full border flex items-center justify-center font-mono text-xs text-white ${m.color}`}>
              {m.initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white">{m.name}</p>
              <p className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider">{m.role}</p>
            </div>
            <div className="w-1.5 h-1.5 rounded-full bg-rogue-emerald shadow-[0_0_6px_rgba(16,185,129,0.6)]" />
          </div>
        ))}
      </div>
    </div>
  );
}

